import {
  Box,
  Flex,
  Text,
  Image,
  Modal,
  Stack,
  Button,
  Heading,
  useTheme,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalContent,
  ModalCloseButton
} from '@homebotapp/hb-react-component-catalog'
import { useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import assumablesHomeIcon from '../../../assets/homes/img/assumable-home-icon.png'
import { selectCustomerFirstName } from '../../../store/selectors/customerProfile'
import { ConfettiDirectMessage } from '../../../Components/shared/ConfettiDirectMessage/ConfettiDirectMessage'

const MSG = defineMessages({
  learnMore: {
    id: 'AffordabilityCalculator.learnMore',
    defaultMessage: 'Learn more'
  },
  learnMoreAbout: {
    id: 'AffordabilityCalculator.learnMoreAbout',
    defaultMessage: 'LEARN MORE ABOUT'
  },
  affordability: {
    id: 'AffordabilityCalculator.affordability',
    defaultMessage: 'Affordability'
  },
  whatIsDebtToIncome: {
    id: 'AffordabilityCalculator.whatIsDebtToIncome',
    defaultMessage: 'What is debt to income (DTI)?'
  },
  dtiRatio: {
    id: 'AffordabilityCalculator.dtiRatio',
    defaultMessage: 'debt-to-income (DTI) ratio'
  },
  dtiRatioExplanation: {
    id: 'AffordabilityCalculator.dtiRatioExplanation',
    defaultMessage:
      'The {dtiRatio} is a percentage that shows how much of your gross monthly income goes toward paying debts.'
  },
  totalDebtPayments: {
    id: 'AffordabilityCalculator.totalDebtPayments',
    defaultMessage:
      'Total debt payments include mortgage/rent, credit card minimums, car loans, student loans, and any other debts.'
  },
  grossMonthlyIncome: {
    id: 'AffordabilityCalculator.grossMonthlyIncome',
    defaultMessage: 'Gross monthly income is your income before taxes and deductions.'
  },
  grossMonthlyIncomeExplanation: {
    id: 'AffordabilityCalculator.grossMonthlyIncomeExplanation',
    defaultMessage:
      'For example, if you make $3,000 per month and have a total of $1,000 in monthly debt payments, your DTI would be 33%'
  },
  twentyEightThirtySixRule: {
    id: 'AffordabilityCalculator.twentyEightThirtySixRule',
    defaultMessage: 'What is the 28/36 rule?'
  },
  twentyEightThirtySixRuleExplanation: {
    id: 'AffordabilityCalculator.twentyEightThirtySixRuleExplanation',
    defaultMessage:
      'The 28/36 rule is a guideline lenders use to decide how much debt you can afford. It’s also a useful tool for you to understand your own affordability and avoid borrowing more than you can comfortably pay off.'
  },
  twoParts: {
    id: 'AffordabilityCalculator.twoParts',
    defaultMessage: 'The rule has two parts:'
  },
  noMoreThanTwentyEight: {
    id: 'AffordabilityCalculator.noMoreThanTwentyEight',
    defaultMessage:
      '✅ No more than 28% of your gross income should go toward housing costs (this includes mortgage, property taxes, insurance, HOA).'
  },
  dtiShouldNotExceedThirtySix: {
    id: 'AffordabilityCalculator.dtiShouldNotExceedThirtySix',
    defaultMessage:
      '✅ Your debt-to-income ratio should not be more than 36%. This means that no more than 36% of your gross income should go toward total debt, including housing and other debts (credit cards, car loans, student loans, etc.).'
  },
  anythingAbove: {
    id: 'AffordabilityCalculator.anythingAbove',
    defaultMessage:
      'Anything that puts you above one or both of these thresholds might be more than you can afford. Make sure to talk to an expert to understand the details of your specific situation. '
  },
  close: {
    id: 'AffordabilityCalculator.close',
    defaultMessage: 'Close'
  },
  affordabilityDm: {
    id: 'AffordabilityCalculator.affordabilityDm',
    defaultMessage: 'Ask {name} about affordability'
  }
})

export interface AffordabilityCalculatorDebtToIncomeLearnMoreModalProps {
  onClose: () => void
}

export const AffordabilityCalculatorDebtToIncomeLearnMoreModal = ({
  onClose
}: AffordabilityCalculatorDebtToIncomeLearnMoreModalProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const customerFirstName = useSelector(selectCustomerFirstName)

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent overflow='hidden' borderRadius={16} marginInline={[0, 5, '20%']} marginTop={[0, 6]} mb={[0, 8]}>
        <ModalHeader
          backgroundSize='cover'
          backgroundBlendMode='lighten'
          _dark={{
            backgroundColor: 'blackAlpha.600'
          }}
        >
          <Flex>
            <Image src={assumablesHomeIcon} aria-hidden='true' h={20} marginLeft={-6} />
            <Box ml={4}>
              <Text size='md' mb={0}>
                {intl.formatMessage(MSG.learnMoreAbout)}
              </Text>
              <Heading as='h3' size='lg' textAlign='left'>
                {intl.formatMessage(MSG.affordability)}
              </Heading>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Heading as='h4' size='md'>
            {intl.formatMessage(MSG.whatIsDebtToIncome)}
          </Heading>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.dtiRatioExplanation, {
              dtiRatio: (
                <Text size='xs' as='span' fontWeight='bold'>
                  {intl.formatMessage(MSG.dtiRatio)}
                </Text>
              )
            })}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.totalDebtPayments)}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.grossMonthlyIncome)}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.grossMonthlyIncomeExplanation)}
          </Text>
          <hr />
          <Heading as='h4' size='md'>
            {intl.formatMessage(MSG.twentyEightThirtySixRule)}
          </Heading>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.twentyEightThirtySixRuleExplanation)}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.twoParts)}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.noMoreThanTwentyEight)}
          </Text>
          <Text size='xs' mt={3}>
            {intl.formatMessage(MSG.dtiShouldNotExceedThirtySix)}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box textAlign='center' margin='auto'>
            <Stack dir='column' spacing={0} align='center'>
              <ConfettiDirectMessage
                topic='debt to income'
                uiContext='Affordability Calculator Page'
                buttonText={intl.formatMessage(MSG.affordabilityDm, { name: customerFirstName })}
                message='I am using the affordability calculator and I need help understanding debt to income ratios and what I can afford.'
              />
              <Button
                mb={2}
                width='100%'
                onClick={onClose}
                tracking={{
                  guid: 'eQ7RwjRE6Yq4UEZyjoeaKQ',
                  ui_context: 'Affordability Calculator Page',
                  descriptor: 'Close learn more modal'
                }}
              >
                {intl.formatMessage(MSG.close)}
              </Button>
            </Stack>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
